import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { detectDefaultValues, autoFixData, startFullProtection } from '../utils/dataProtection';
import { getStoreSettings } from '../services/storeSettingsService';

const DataProtectionPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [protectionActive, setProtectionActive] = useState(false);
  const [detectedIssues, setDetectedIssues] = useState<string[]>([]);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [autoFixEnabled, setAutoFixEnabled] = useState(true);

  // فحص البيانات عند تحميل الصفحة
  useEffect(() => {
    checkDataIntegrity();
  }, []);

  const checkDataIntegrity = async () => {
    try {
      setChecking(true);
      console.log('🔍 فحص سلامة البيانات...');
      
      // الحصول على إعدادات المتجر
      const settings = await getStoreSettings();
      
      // فحص القيم الافتراضية
      const issues = detectDefaultValues(settings);
      setDetectedIssues(issues);
      setLastCheck(new Date());
      
      if (issues.length > 0) {
        console.warn('🚨 تم اكتشاف مشاكل في البيانات:', issues);
      } else {
        console.log('✅ البيانات سليمة');
      }
      
    } catch (error) {
      console.error('❌ خطأ في فحص البيانات:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleAutoFix = async () => {
    try {
      setLoading(true);
      console.log('🔧 بدء الإصلاح التلقائي...');
      
      const fixed = await autoFixData();
      
      if (fixed) {
        alert('✅ تم إصلاح البيانات بنجاح!');
        // إعادة فحص البيانات
        await checkDataIntegrity();
        
        // إعادة تحميل الصفحة لتطبيق التغييرات
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        alert('ℹ️ لا توجد مشاكل تحتاج للإصلاح');
      }
      
    } catch (error) {
      console.error('❌ خطأ في الإصلاح التلقائي:', error);
      alert('❌ حدث خطأ أثناء الإصلاح');
    } finally {
      setLoading(false);
    }
  };

  const toggleProtection = () => {
    if (protectionActive) {
      // إيقاف الحماية
      setProtectionActive(false);
      console.log('🛑 تم إيقاف نظام الحماية');
    } else {
      // تفعيل الحماية
      startFullProtection();
      setProtectionActive(true);
      console.log('🛡️ تم تفعيل نظام الحماية');
    }
  };

  const getStatusColor = () => {
    if (detectedIssues.length === 0) return 'success';
    if (detectedIssues.length <= 2) return 'warning';
    return 'error';
  };

  const getStatusIcon = () => {
    if (detectedIssues.length === 0) return <CheckIcon color="success" />;
    if (detectedIssues.length <= 2) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const getStatusText = () => {
    if (detectedIssues.length === 0) return 'البيانات سليمة';
    if (detectedIssues.length <= 2) return 'مشاكل بسيطة';
    return 'مشاكل خطيرة';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <SecurityIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            حماية البيانات من القيم الافتراضية
          </Typography>
          <Typography variant="body1" color="text.secondary">
            نظام مراقبة وحماية شامل لضمان عدم عودة البيانات للقيم الافتراضية
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* حالة النظام */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {getStatusIcon()}
                <Typography variant="h6" sx={{ mt: 1 }}>
                  حالة البيانات
                </Typography>
                <Chip 
                  label={getStatusText()} 
                  color={getStatusColor()} 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <ShieldIcon color={protectionActive ? 'success' : 'disabled'} />
                <Typography variant="h6" sx={{ mt: 1 }}>
                  نظام الحماية
                </Typography>
                <Chip 
                  label={protectionActive ? 'مفعل' : 'معطل'} 
                  color={protectionActive ? 'success' : 'default'} 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <VisibilityIcon color="primary" />
                <Typography variant="h6" sx={{ mt: 1 }}>
                  آخر فحص
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {lastCheck ? lastCheck.toLocaleString('ar-SA') : 'لم يتم الفحص بعد'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* المشاكل المكتشفة */}
        {detectedIssues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              تم اكتشاف {detectedIssues.length} مشكلة في البيانات:
            </Typography>
            <List dense>
              {detectedIssues.map((issue, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={issue}
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* عدم وجود مشاكل */}
        {detectedIssues.length === 0 && lastCheck && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6">
              ✅ ممتاز! لا توجد قيم افتراضية في البيانات
            </Typography>
            <Typography variant="body2">
              جميع بيانات المتجر محدثة وصحيحة
            </Typography>
          </Alert>
        )}

        {/* أزرار التحكم */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                size="large"
                startIcon={checking ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={checkDataIntegrity}
                disabled={checking}
                sx={{ minWidth: 180 }}
              >
                {checking ? 'جاري الفحص...' : 'فحص البيانات'}
              </Button>
            </Grid>
            
            {detectedIssues.length > 0 && (
              <Grid item>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
                  onClick={handleAutoFix}
                  disabled={loading}
                  sx={{ minWidth: 180 }}
                >
                  {loading ? 'جاري الإصلاح...' : 'إصلاح تلقائي'}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* إعدادات الحماية */}
        <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            إعدادات الحماية
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={protectionActive}
                onChange={toggleProtection}
                color="primary"
              />
            }
            label="تفعيل نظام الحماية التلقائي"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={autoFixEnabled}
                onChange={(e) => setAutoFixEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="الإصلاح التلقائي عند اكتشاف مشاكل"
            sx={{ ml: 3 }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            نظام الحماية يراقب البيانات في الوقت الفعلي ويمنع حفظ القيم الافتراضية
          </Typography>
        </Box>

        {/* معلومات إضافية */}
        <Box sx={{ mt: 4, p: 3, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            كيف يعمل نظام الحماية؟
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="مراقبة البيانات في الوقت الفعلي" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="منع حفظ القيم الافتراضية في localStorage" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="فحص دوري كل 30 ثانية" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="إصلاح تلقائي عند اكتشاف مشاكل" />
            </ListItem>
          </List>
        </Box>
      </Paper>
    </Container>
  );
};

export default DataProtectionPage;
